import { useProfile } from "@/hooks/useProfile";
import { isActivePlanWeek } from "@/lib/daycare-hotel/planFixedDays";
import {
  useStayPlanContracts,
  partitionStayPlanContracts,
  type StayPlanContract,
} from "@/hooks/useStayPlanContracts";
import {
  getBillingCycle,
  getCycleQuota,
  getFinancialStatus,
  getStayPlanAlerts,
  getStayPlanBalance,
  getStayPlanPaymentStatus,
  getWeeklyQuotaStatus,
  type DateRange,
  type StayPlanAlertKind,
  type StayPlanBalance,
  type StayPlanFinancialStatus,
  type StayPlanModule,
  type StayPlanPaymentStatus,
  type WeeklyQuotaStatus,
} from "@/lib/daycare-hotel/stayPlans";
import { getEffectiveWeeklyLimit } from "@/lib/daycare-hotel/weeklyPlanRemoval";

export interface StayPlanStatus {
  contract: StayPlanContract;
  /** `null` em pacote de diárias: ele não tem ciclo de cobrança. */
  cycle: DateRange | null;
  quota: number | null;
  balance: StayPlanBalance;
  financialStatus: StayPlanFinancialStatus;
  alerts: StayPlanAlertKind[];
  /** A venda da charge do ciclo corrente está `PAGA`. `false` sem nenhuma charge. */
  isPaid: boolean;
  /** Venda em aberto a cobrar: a vencida, ou a do ciclo corrente. `null` quando não há o que cobrar. */
  openChargeSaleId: string | null;
  /** Eixo de dinheiro, independente de `financialStatus` (vigência). */
  paymentStatus: StayPlanPaymentStatus;
  /** Valor da cobrança em aberto deste contrato; `null` quando não há o que cobrar. */
  openAmount: number | null;
  /** Vencimento da cobrança em aberto; `null` quando não há o que cobrar. */
  dueOn: string | null;
  usageSummary: { used: number; limit: number; period: "week" | "cycle" } | null;
  /** Cota da semana corrente em plano `per_week`; `null` nos demais. */
  weeklyQuota: WeeklyQuotaStatus | null;
  /** Diárias dadas ou tiradas à mão dentro do ciclo corrente. */
  adjustmentUnits: number;
}

export function buildStayPlanStatus(contract: StayPlanContract, today: string): StayPlanStatus {
  const isRecurring = contract.kind === "recurring";
  const cycle = isRecurring && contract.firstDueOn ? getBillingCycle(contract.firstDueOn, today) : null;

  const quota = isRecurring && contract.firstDueOn && contract.usageRule
    ? getCycleQuota({ usageRule: contract.usageRule, usageLimit: contract.usageLimit })
    : null;

  const inCycle = (usedOn: string) => !cycle || (usedOn >= cycle.start && usedOn <= cycle.end);
  const sum = (source: "hold" | "checkin") =>
    contract.usages
      .filter((usage) => usage.source === source && inCycle(usage.usedOn))
      .reduce((total, usage) => total + usage.units, 0);

  // `carryover_expires_on` é o fim do ciclo em que o crédito de reposição pode
  // ser gasto. Passou disso, ele não conta mais para o saldo.
  const carryoverExpired = Boolean(contract.carryoverExpiresOn && today > contract.carryoverExpiresOn);

  // Só o ajuste do ciclo corrente conta: ajuste expira com o ciclo em que foi
  // dado. Em pacote de diárias (sem ciclo) o ajuste já foi aplicado em
  // remaining_days pela RPC, então não entra aqui.
  const adjustmentUnits = cycle
    ? contract.adjustments
        .filter((adjustment) => adjustment.cycleStartsOn === cycle.start && !adjustment.targetWeekStartsOn)
        .reduce((total, adjustment) => total + adjustment.delta, 0)
    : 0;

  const weeklyQuota: WeeklyQuotaStatus | null =
    isRecurring && contract.usageRule === "per_week" && contract.usageLimit !== null
      ? (() => {
          const base = getWeeklyQuotaStatus({ usageLimit: contract.usageLimit, usages: contract.usages, reference: today });
          return getWeeklyQuotaStatus({
            usageLimit: contract.usageLimit,
            // Semana de folga do quinzenal: a cota é zero, qualquer diária é extra.
            effectiveLimit: isActivePlanWeek(contract.startsOn, today, contract.weekInterval ?? 1)
              ? getEffectiveWeeklyLimit({
                  usageLimit: contract.usageLimit,
                  weekStartsOn: base.week.start,
                  adjustments: contract.adjustments,
                })
              : 0,
            usages: contract.usages,
            reference: today,
          });
        })()
      : null;

  const usageSummary = isRecurring && contract.usageRule === "per_month" && contract.usageLimit !== null && cycle
    ? {
        used: contract.usages.filter((usage) => usage.usedOn >= cycle.start && usage.usedOn <= cycle.end).reduce((total, usage) => total + usage.units, 0),
        limit: contract.usageLimit,
        period: "cycle" as const,
      }
    : weeklyQuota
      ? { used: weeklyQuota.usedThisWeek, limit: weeklyQuota.effectiveLimit, period: "week" as const }
      : null;

  const balance = getStayPlanBalance({
    quota,
    carryoverUnits: carryoverExpired ? 0 : contract.carryoverUnits,
    adjustmentUnits,
    checkins: sum("checkin"),
    holds: sum("hold"),
  });

  const currentCharge = cycle
    ? contract.charges.find((charge) => charge.periodStartsOn >= cycle.start && charge.periodStartsOn <= cycle.end)
    : contract.charges[0] ?? null;

  // `< today`, não `<=`: uma charge que vence hoje ainda não está atrasada — é
  // `due_today` (ver `getFinancialStatus`), mesmo que a venda ainda não esteja paga.
  const overdueCharge = contract.charges.find((charge) => charge.dueOn < today && charge.saleStatus !== "PAGA") ?? null;

  const financialStatus = getFinancialStatus(
    { nextDueOn: contract.nextDueOn, hasOverdueCharge: Boolean(overdueCharge) },
    today,
  );

  // A charge que responde por "o dinheiro entrou?": a vencida tem prioridade
  // sobre a do ciclo corrente, porque é ela que precisa de ação.
  const payableCharge = overdueCharge ?? currentCharge ?? null;
  const paymentStatus = getStayPlanPaymentStatus(
    { billingTiming: contract.billingTiming, charge: payableCharge },
    today,
  );
  const hasOpenCharge = payableCharge !== null && paymentStatus !== "paid" && paymentStatus !== "none";
  // Vale também para pacote: contratado pela reserva com cobrança no final, a
  // charge só nasce na hora de cobrar, e sem isso o plano ficava sem nenhuma
  // mensalidade à vista.
  const hasUnbilledPlan = contract.status === "active"
    && payableCharge === null
    && contract.nextDueOn !== null;

  return {
    contract,
    cycle,
    quota,
    balance,
    financialStatus,
    alerts: getStayPlanAlerts({
      kind: contract.kind,
      financialStatus,
      projectedBalance: balance.projected,
      remainingDays: contract.remainingDays,
      weeklyQuota,
    }),
    isPaid: currentCharge?.saleStatus === "PAGA",
    paymentStatus: hasUnbilledPlan
      ? (contract.billingTiming === "end" ? "scheduled" : "awaiting")
      : paymentStatus,
    openAmount: hasOpenCharge || hasUnbilledPlan ? (payableCharge?.amount ?? contract.price) : null,
    dueOn: hasOpenCharge ? payableCharge!.dueOn : hasUnbilledPlan ? contract.nextDueOn : null,
    openChargeSaleId: hasOpenCharge ? payableCharge!.saleId : null,
    usageSummary,
    weeklyQuota,
    adjustmentUnits,
  };
}

export function selectStayPlanStatusContract(
  contracts: StayPlanContract[],
  date: string,
  contractId?: string | null,
) {
  const activeContracts = partitionStayPlanContracts(contracts).active
    .filter((contract) => contract.startsOn <= date);

  if (contractId) return activeContracts.find((contract) => contract.id === contractId) ?? null;

  return activeContracts.sort((a, b) => b.startsOn.localeCompare(a.startsOn))[0] ?? null;
}

/** Estado do plano ativo de um pet naquela data. `status === null` = pet sem plano. */
export function useStayPlanStatus({
  module,
  petId,
  date,
  contractId,
}: {
  module: StayPlanModule;
  petId: string | null;
  date: string;
  contractId?: string | null;
}) {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const query = useStayPlanContracts(module, clinicId, petId ? [petId] : []);
  const active = selectStayPlanStatusContract(query.data ?? [], date, contractId);

  return {
    status: active ? buildStayPlanStatus(active, date) : null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
